import { Injectable } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { ProjectType } from '../_models/projectType';
import { COUNTRY_CODES, FEATURED_COUNTRY_CODES } from '../_utils/countries';

@Injectable({
  providedIn: 'root'
})
export class LabelI18nService {
  private static readonly PROJECT_TYPES: Record<string, string> = {
    SpecialFoundations: 'Foundations work',
    RoadStructure: 'Civil engineering work',
    Road: 'Roads and networks',
    Earthwork: 'Earthwork',
    ExplosiveWorks: 'Blasting work',
    PipingWork: 'Pipework',
    RailWork: 'Railway works',
    ElectricWork: 'Electrical work',
    MaritimeWork: 'Work at maritime or river sites',
    UndergroundWork: 'Underground works'
  };

  private static readonly PROJECT_TYPE_CODES_BY_FR_NAME: Record<string, string> = {
    'Route/VRD': 'Road',
    Terrassement: 'Earthwork',
    'Fondations spéciales': 'SpecialFoundations',
    "Ouvrages d'art": 'RoadStructure',
    "Travaux à l'explosif": 'ExplosiveWorks',
    'Travaux de canalisation': 'PipingWork',
    'Travaux de voies ferrées': 'RailWork',
    'Travaux électriques': 'ElectricWork',
    'Travaux en site maritime ou fluvial': 'MaritimeWork',
    'Travaux souterrains': 'UndergroundWork'
  };

  private static readonly INDICATORS: Record<string, string> = {
    EN: 'Energy',
    CO: 'GHG Emissions',
    GN: 'Natural Aggregates',
    AE: 'Coated aggregates',
    TK: 'Tonne.kilometre',
    DRC: 'Spoils reused within the project',
    MR: 'Recycled materials',
    PR: 'Resource preservation',
    PE: 'Water preservation',
    PB: 'Biodiversity preservation',
    TR: 'Tonnes transported by road'
  };

  private static readonly SYNTHESE_LABELS: Record<string, string> = {
    'Extraction des matériaux': 'Material extraction',
    'Transports en amont': 'Upstream transport',
    'Fabrication des mélanges': 'Production',
    'Fret entrant sur le chantier': 'Inward freight to the site',
    'Mise en oeuvre': 'Implementation',
    'Fret sortant du chantier': 'Freight leaving the site',
    'Sous-total': 'Sub-total',
    'Comparaison / Base': 'Comparison / Base',
    'Granulats Naturels': 'Natural Aggregates',
    'Granulats naturels': 'Natural Aggregates',
    'Matériaux recyclés': 'Recycled materials',
    "Utilisation d'Agrégats d'enrobés": 'Use of coated aggregates',
    "Utilisation d'agrégats d'enrobés": 'Use of coated aggregates',
    'Déblais réutilisés au sein du projet': 'Spoils reused within the project',
    'Indicateur : Energie « procédé »': 'Indicator: Energy',
    'Indicateur : émission de Gaz à Effet de Serre': 'Indicator: GHG Emissions',
    'Indicateur : Préservation de la ressource': 'Indicator: Resource preservation',
    'Indicateur : Tonne.kilomètre': 'Indicator: Tonne.kilometre',
    'Indicateurs déclaratif : Gestion de l’eau': 'Declarative indicator: Water management',
    'Indicateurs déclaratif : Préservation de la biodiversité': 'Declarative indicator: Biodiversity preservation',
    '(en Méga-Joules)': '(in Mega-Joules)',
    '(en tonnes équivalent CO2)': '(in tonnes of CO2 equivalent)',
    '(en tonnes)': '(in tonnes)',
    '(en tonne.kilomètre)': '(in tonne.kilometre)',
    "Gestion de l'eau": 'Water management',
    'Préservation de la biodiversité': 'Biodiversity preservation',
    "Préservation de l'eau": 'Water preservation',
    'Préservation de la ressource': 'Resource preservation',
    "Niveau d'engagement": 'Commitment level',
    Engagement: 'Commitment',
    Justificatif: 'Justification',
    'Scénario par défaut': 'Default scenario'
  };

  constructor(private translate: TranslateService) {}

  get locale(): string {
    return this.translate.currentLang === 'en' ? 'en-US' : 'fr-FR';
  }

  exponential(value: number, fractionDigits: number): string {
    const formatted = value.toExponential(fractionDigits);
    return this.translate.currentLang === 'en' ? formatted : formatted.replace('.', ',');
  }

  countryName(code: string): string {
    // TypeScript 4.3 ne déclare pas Intl.DisplayNames
    return new (Intl as any).DisplayNames([this.locale], { type: 'region' }).of(code) ?? code;
  }

  get featuredCountries(): { code: string; name: string }[] {
    return FEATURED_COUNTRY_CODES.map((code) => ({ code, name: this.countryName(code) }));
  }

  get countries(): { code: string; name: string }[] {
    return COUNTRY_CODES.map((code) => ({ code, name: this.countryName(code) })).sort((a, b) => a.name.localeCompare(b.name, this.locale));
  }

  projectTypeName(code?: string, fallback?: string): string | undefined {
    if (this.translate.currentLang === 'en' || !code) return this.localize(LabelI18nService.PROJECT_TYPES, code, fallback);
    // Les listes sont traduites au chargement : repassé en français, le libellé reçu peut déjà être l'anglais
    const frenchName = Object.keys(LabelI18nService.PROJECT_TYPE_CODES_BY_FR_NAME).find((name) => LabelI18nService.PROJECT_TYPE_CODES_BY_FR_NAME[name] === code);
    return frenchName ?? fallback;
  }

  indicatorName(code?: string, fallback?: string): string | undefined {
    return this.localize(LabelI18nService.INDICATORS, code, fallback);
  }

  indicatorLabel(indicator?: { code?: string; displayName?: string }): string {
    return this.indicatorName(indicator?.code, indicator?.displayName) ?? '';
  }

  indicatorLabelWithUnit(indicator?: { code?: string; displayName?: string; workUnityDisplayShortName?: string }): string {
    if (!indicator) return '';
    return `${this.indicatorLabel(indicator)} (${indicator.workUnityDisplayShortName})`;
  }

  syntheseLabel(label?: string): string | undefined {
    if (this.translate.currentLang !== 'en' || !label) return label;
    const projectTypeCode = LabelI18nService.PROJECT_TYPE_CODES_BY_FR_NAME[label];
    if (projectTypeCode) return this.projectTypeName(projectTypeCode, label);
    return LabelI18nService.SYNTHESE_LABELS[label] ?? label;
  }

  localizeProjectTypes(projectTypes: ProjectType[]): ProjectType[] {
    if (this.translate.currentLang !== 'en') return projectTypes ?? [];
    return (projectTypes ?? []).map((projectType) => ({ ...projectType, displayName: this.projectTypeName(projectType.code, projectType.displayName) }));
  }

  private localize(labels: Record<string, string>, code?: string, fallback?: string): string | undefined {
    if (this.translate.currentLang !== 'en' || !code) return fallback;
    return labels[code] ?? fallback;
  }
}
